import {
  Children,
  ComponentProps,
  FC,
  ReactElement,
  ReactNode,
  cloneElement,
  createContext,
  isValidElement,
  useContext,
} from 'react';
import { Accordion as RadixAccordion } from 'radix-ui';
import { twMerge } from 'tailwind-merge';
import { Typography } from '../../Typography';
import { ChevronUp } from '../../svgs';

export type JoinLeagueCardVariant = 'public' | 'sponsor';
export type JoinLeagueCardSize = 'small' | 'large' | 'responsive';

type JoinLeagueCardSlotName =
  | 'title'
  | 'pill'
  | 'meta'
  | 'detailMeta'
  | 'code'
  | 'description'
  | 'action';

interface JoinLeagueCardContextValue {
  size: JoinLeagueCardSize;
  variant: JoinLeagueCardVariant;
}

const JoinLeagueCardContext = createContext<JoinLeagueCardContextValue>({
  size: 'responsive',
  variant: 'public',
});

const useJoinLeagueCardContext = () => useContext(JoinLeagueCardContext);

const sizeClassMap = {
  cardPadding: {
    small: 'p-[16px]',
    large: 'p-[20px]',
    responsive: 'p-[16px] md:p-[20px]',
  },
  title: {
    small: 'text-20 leading-24',
    large: 'text-24 leading-28',
    responsive: 'text-20 leading-24 md:text-24 md:leading-28',
  },
  metaWrapGap: {
    small: 'gap-x-[16px] gap-y-[4px]',
    large: 'gap-x-[20px] gap-y-[4px]',
    responsive: 'gap-x-[16px] gap-y-[4px] md:gap-x-[20px]',
  },
  headerMetaGap: {
    small: 'gap-x-[16px] gap-y-[4px]',
    large: 'gap-x-[20px] gap-y-[4px]',
    responsive: 'gap-x-[16px] gap-y-[4px] md:gap-x-[20px]',
  },
  triggerGap: {
    small: 'gap-[16px]',
    large: 'gap-[12px]',
    responsive: 'gap-[16px] md:gap-[12px]',
  },
  metaText: {
    small: 'text-12 leading-16',
    large: 'text-14 leading-20',
    responsive: 'text-12 leading-16 md:text-14 md:leading-20',
  },
  descriptionRowGap: {
    small: 'gap-[16px]',
    large: 'gap-[20px]',
    responsive: 'gap-[16px] md:gap-[20px]',
  },
  sponsorBottomRowGap: {
    small: 'gap-[8px]',
    large: 'gap-[20px]',
    responsive: 'gap-[8px] md:gap-[20px]',
  },
  codeRowGap: {
    small: 'gap-[8px]',
    large: 'gap-[16px]',
    responsive: 'gap-[8px] md:gap-[16px]',
  },
  chevronSize: {
    small: 'size-[20px]',
    large: 'size-[24px]',
    responsive: 'size-[20px] md:size-[24px]',
  },
} as const;

const contentTopGapClassMap: Record<JoinLeagueCardVariant, Record<JoinLeagueCardSize, string>> = {
  public: {
    small: 'pt-[12px]',
    large: 'pt-[16px]',
    responsive: 'pt-[12px] md:pt-[16px]',
  },
  sponsor: {
    small: 'pt-[16px]',
    large: 'pt-[16px]',
    responsive: 'pt-[16px]',
  },
};

const surfaceClassMap: Record<JoinLeagueCardVariant, string> = {
  public: 'bg-[white]',
  sponsor: 'bg-[#242651]',
};

const dividerClassMap: Record<JoinLeagueCardVariant, string> = {
  public: 'border-[#eae9fd]',
  sponsor: 'border-[#4c5094]',
};

const headingColorMap: Record<JoinLeagueCardVariant, string> = {
  public: 'text-[#222526]',
  sponsor: 'text-[#2efcad]',
};

const textColorMap: Record<JoinLeagueCardVariant, string> = {
  public: 'text-[#222526]',
  sponsor: 'text-[white]',
};

const copyColorMap: Record<JoinLeagueCardVariant, string> = {
  public: 'text-[#0072ee]',
  sponsor: 'text-[#2efcad]',
};

const pillClassMap: Record<JoinLeagueCardVariant, string> = {
  public: 'bg-[#eae9fd] text-[#222526]',
  sponsor: 'bg-[#2efcad] text-[#242651]',
};

const actionClassMap: Record<JoinLeagueCardVariant, string> = {
  public: 'bg-[#0072ee] text-[white]',
  sponsor: 'bg-[white] text-[#222526]',
};

interface JoinLeagueCardProps extends Omit<ComponentProps<'article'>, 'children'> {
  variant?: JoinLeagueCardVariant;
  size?: JoinLeagueCardSize;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  children: ReactNode;
}

interface JoinLeagueCardTitleProps extends ComponentProps<'p'> {
  children: ReactNode;
}

interface JoinLeagueCardPillProps extends ComponentProps<'span'> {
  children?: ReactNode;
}

interface JoinLeagueCardMetaProps extends ComponentProps<'div'> {
  children: ReactNode;
}

interface JoinLeagueCardMetaItemProps extends ComponentProps<'div'> {
  label: string;
  value: string | number;
}

interface JoinLeagueCardDetailMetaProps extends ComponentProps<'div'> {
  children: ReactNode;
}

interface JoinLeagueCardDetailMetaItemProps extends ComponentProps<'div'> {
  label: string;
  value: string | number;
}

interface JoinLeagueCardCodeProps extends Omit<ComponentProps<'div'>, 'onCopy'> {
  label?: string;
  value: string;
  copyLabel?: string;
  showCopyIcon?: boolean;
  onCopy?: (value: string) => void;
}

interface JoinLeagueCardDescriptionProps extends ComponentProps<'p'> {
  children: ReactNode;
}

interface JoinLeagueCardActionProps extends ComponentProps<'button'> {
  children: ReactNode;
}

type SlotComponent<T> = FC<T> & {
  __joinLeagueCardSlot: JoinLeagueCardSlotName;
};

type SlotElement = ReactElement<{ className?: string }>;

const getSlotName = (child: ReactNode): JoinLeagueCardSlotName | undefined => {
  if (!isValidElement(child)) return undefined;
  return (child.type as SlotComponent<unknown>).__joinLeagueCardSlot;
};

const cloneWithClassName = (
  slotElement: SlotElement | undefined,
  className: string,
  key: string
): SlotElement | null => {
  if (!slotElement) return null;
  return cloneElement(slotElement, {
    key,
    className: twMerge(slotElement.props.className, className),
  });
};

const Title: SlotComponent<JoinLeagueCardTitleProps> = ({ className, children, ...otherProps }) => {
  const { size, variant } = useJoinLeagueCardContext();

  return (
    <Typography
      as="h3"
      variant="headline025"
      className={twMerge(
        'w-full truncate',
        sizeClassMap.title[size],
        headingColorMap[variant],
        className
      )}
      {...otherProps}
    >
      {children}
    </Typography>
  );
};
Title.__joinLeagueCardSlot = 'title';

const Pill: SlotComponent<JoinLeagueCardPillProps> = ({ className, children, ...otherProps }) => {
  const { variant } = useJoinLeagueCardContext();
  const content = children ?? (variant === 'sponsor' ? 'Paid' : 'Public');

  return (
    <span
      className={twMerge(
        'inline-flex items-center justify-center rounded-[4px] px-[8px] py-[2px]',
        pillClassMap[variant],
        className
      )}
      {...otherProps}
    >
      <Typography as="span" variant="label020" className="font-bold">
        {content}
      </Typography>
    </span>
  );
};
Pill.__joinLeagueCardSlot = 'pill';

const Meta: SlotComponent<JoinLeagueCardMetaProps> = ({ className, children, ...otherProps }) => {
  const { size } = useJoinLeagueCardContext();

  return (
    <div
      className={twMerge('flex flex-wrap items-center', sizeClassMap.metaWrapGap[size], className)}
      {...otherProps}
    >
      {children}
    </div>
  );
};
Meta.__joinLeagueCardSlot = 'meta';

const MetaItem: FC<JoinLeagueCardMetaItemProps> = ({ className, label, value, ...otherProps }) => {
  const { size, variant } = useJoinLeagueCardContext();

  return (
    <div className={twMerge('inline-flex items-center gap-[4px]', className)} {...otherProps}>
      <Typography
        as="span"
        variant="paragraph010"
        className={twMerge(sizeClassMap.metaText[size], textColorMap[variant])}
      >
        {label}
      </Typography>
      <Typography
        as="span"
        variant="subheadline010"
        className={twMerge(sizeClassMap.metaText[size], textColorMap[variant])}
      >
        {value}
      </Typography>
    </div>
  );
};

const DetailMeta: SlotComponent<JoinLeagueCardDetailMetaProps> = ({
  className,
  children,
  ...otherProps
}) => {
  const { size } = useJoinLeagueCardContext();

  return (
    <div
      className={twMerge('flex flex-wrap items-center', sizeClassMap.metaWrapGap[size], className)}
      {...otherProps}
    >
      {children}
    </div>
  );
};
DetailMeta.__joinLeagueCardSlot = 'detailMeta';

const DetailMetaItem: FC<JoinLeagueCardDetailMetaItemProps> = ({
  className,
  label,
  value,
  ...otherProps
}) => {
  const { size, variant } = useJoinLeagueCardContext();

  return (
    <div className={twMerge('inline-flex items-center gap-[4px]', className)} {...otherProps}>
      <Typography
        as="span"
        variant="paragraph010"
        className={twMerge(sizeClassMap.metaText[size], textColorMap[variant])}
      >
        {label}
      </Typography>
      <Typography
        as="span"
        variant="subheadline010"
        className={twMerge(sizeClassMap.metaText[size], textColorMap[variant])}
      >
        {value}
      </Typography>
    </div>
  );
};

const Code: SlotComponent<JoinLeagueCardCodeProps> = ({
  className,
  label = 'League Code:',
  value,
  copyLabel = 'Copy Code',
  showCopyIcon = true,
  onCopy,
  ...otherProps
}) => {
  const { size, variant } = useJoinLeagueCardContext();

  const handleCopy = async () => {
    onCopy?.(value);
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;

    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Intentionally no-op for environments where clipboard access is blocked.
    }
  };

  return (
    <div
      className={twMerge(
        'flex items-center',
        sizeClassMap.codeRowGap[size],
        size === 'small'
          ? 'w-full justify-between'
          : size === 'large'
            ? 'justify-start'
            : 'w-full justify-between md:w-auto md:justify-start',
        className
      )}
      {...otherProps}
    >
      <div
        className={twMerge(
          'inline-flex min-w-0 items-center gap-[4px]',
          size === 'large' ? 'shrink-0' : 'flex-1'
        )}
      >
        <Typography
          as="span"
          variant="paragraph010"
          className={twMerge(
            sizeClassMap.metaText[size],
            textColorMap[variant],
            'whitespace-nowrap'
          )}
        >
          {label}
        </Typography>
        <Typography
          as="span"
          variant="subheadline010"
          className={twMerge(
            sizeClassMap.metaText[size],
            textColorMap[variant],
            'whitespace-nowrap'
          )}
        >
          {value}
        </Typography>
      </div>
      <button
        type="button"
        onClick={handleCopy}
        className={twMerge(
          'inline-flex shrink-0 items-center gap-[8px] p-0',
          size === 'small' ? 'h-[16px]' : size === 'large' ? 'h-[20px]' : 'h-[16px] md:h-[20px]',
          copyColorMap[variant]
        )}
      >
        <Typography as="span" variant="button020" className={copyColorMap[variant]}>
          {copyLabel}
        </Typography>
        {showCopyIcon && (
          <span className="relative inline-flex size-[24px]">
            <span
              className={twMerge(
                'absolute left-[8px] top-[3px] size-[11px] rounded-[2px] border',
                variant === 'sponsor' ? 'border-[#2efcad]' : 'border-[#0072ee]'
              )}
            />
            <span
              className={twMerge(
                'absolute left-[5px] top-[7px] size-[11px] rounded-[2px] border bg-transparent',
                variant === 'sponsor' ? 'border-[#2efcad]' : 'border-[#0072ee]'
              )}
            />
          </span>
        )}
      </button>
    </div>
  );
};
Code.__joinLeagueCardSlot = 'code';

const Description: SlotComponent<JoinLeagueCardDescriptionProps> = ({
  className,
  children,
  ...otherProps
}) => {
  const { size, variant } = useJoinLeagueCardContext();

  return (
    <Typography
      as="p"
      variant="paragraph010"
      className={twMerge('w-full', sizeClassMap.metaText[size], textColorMap[variant], className)}
      {...otherProps}
    >
      {children}
    </Typography>
  );
};
Description.__joinLeagueCardSlot = 'description';

const Action: SlotComponent<JoinLeagueCardActionProps> = ({
  className,
  children,
  type = 'button',
  ...otherProps
}) => {
  const { variant } = useJoinLeagueCardContext();

  return (
    <button
      type={type}
      className={twMerge(
        'inline-flex items-center justify-center rounded-[4px] px-[16px] py-[10px]',
        actionClassMap[variant],
        className
      )}
      {...otherProps}
    >
      <Typography as="span" variant="button020" className={actionClassMap[variant]}>
        {children}
      </Typography>
    </button>
  );
};
Action.__joinLeagueCardSlot = 'action';

type JoinLeagueCardComponent = FC<JoinLeagueCardProps> & {
  Title: typeof Title;
  Pill: typeof Pill;
  Meta: typeof Meta;
  MetaItem: typeof MetaItem;
  DetailMeta: typeof DetailMeta;
  DetailMetaItem: typeof DetailMetaItem;
  Code: typeof Code;
  Description: typeof Description;
  Action: typeof Action;
};

const ITEM_VALUE = 'join-league-card-item';

export const JoinLeagueCard: JoinLeagueCardComponent = ({
  className,
  variant = 'public',
  size = 'responsive',
  open,
  defaultOpen = false,
  onOpenChange,
  disabled,
  children,
  ...otherProps
}) => {
  const slots: Partial<Record<JoinLeagueCardSlotName, SlotElement>> = {};

  Children.forEach(children, child => {
    const slotName = getSlotName(child);
    if (!slotName || !isValidElement(child) || slots[slotName]) return;
    slots[slotName] = child as SlotElement;
  });

  const isControlled = typeof open === 'boolean';
  const accordionValue = open ? ITEM_VALUE : '';
  const isSponsor = variant === 'sponsor';
  const isSmall = size === 'small';
  const isLarge = size === 'large';
  const isResponsive = size === 'responsive';

  const sponsorDesktopAction =
    isSponsor && slots.action
      ? cloneWithClassName(
          slots.action,
          size === 'small'
            ? 'hidden'
            : size === 'large'
              ? 'inline-flex shrink-0'
              : 'hidden md:inline-flex md:shrink-0',
          'sponsor-desktop-action'
        )
      : null;

  const sponsorMobileAction =
    isSponsor && slots.action
      ? cloneWithClassName(
          slots.action,
          size === 'large'
            ? 'hidden'
            : size === 'small'
              ? 'inline-flex shrink-0'
              : 'inline-flex shrink-0 md:hidden',
          'sponsor-mobile-action'
        )
      : null;

  const renderDivider = () => (
    <div className={twMerge('h-0 w-full border-t', dividerClassMap[variant])} />
  );

  return (
    <JoinLeagueCardContext.Provider value={{ size, variant }}>
      <RadixAccordion.Root
        type="single"
        collapsible
        value={isControlled ? accordionValue : undefined}
        defaultValue={isControlled ? undefined : defaultOpen ? ITEM_VALUE : undefined}
        onValueChange={value => onOpenChange?.(value === ITEM_VALUE)}
        className="w-full"
      >
        <RadixAccordion.AccordionItem value={ITEM_VALUE} className="w-full border-none">
          <article
            className={twMerge(
              'w-full overflow-hidden rounded-[12px]',
              sizeClassMap.cardPadding[size],
              surfaceClassMap[variant],
              className
            )}
            {...otherProps}
          >
            <RadixAccordion.Header className="flex w-full">
              <RadixAccordion.AccordionTrigger
                disabled={disabled}
                className={twMerge(
                  'group flex w-full items-center justify-between text-left',
                  sizeClassMap.triggerGap[size],
                  disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                )}
              >
                <div className="flex min-w-0 flex-1 flex-col gap-[12px]">
                  {slots.title}
                  {(slots.pill || slots.meta) && (
                    <div
                      className={twMerge(
                        'flex flex-wrap items-center',
                        sizeClassMap.headerMetaGap[size]
                      )}
                    >
                      {slots.pill}
                      {slots.meta}
                    </div>
                  )}
                </div>
                <ChevronUp
                  className={twMerge(
                    'shrink-0 rotate-180 transition-transform duration-200 ease-out group-data-[state=open]:rotate-0',
                    sizeClassMap.chevronSize[size],
                    variant === 'sponsor' ? 'text-[white]' : 'text-[#222526]'
                  )}
                  aria-hidden
                />
              </RadixAccordion.AccordionTrigger>
            </RadixAccordion.Header>

            <RadixAccordion.AccordionContent className="overflow-hidden data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown">
              <div className={twMerge('flex flex-col', contentTopGapClassMap[variant][size])}>
                {!isSponsor && isSmall && (
                  <div className="flex flex-col gap-[12px]">
                    {renderDivider()}
                    {slots.detailMeta && (
                      <div className="flex w-full items-center">{slots.detailMeta}</div>
                    )}
                    {renderDivider()}
                    {slots.code && (
                      <div
                        className={twMerge(
                          'flex w-full items-center',
                          sizeClassMap.codeRowGap[size]
                        )}
                      >
                        {slots.code}
                      </div>
                    )}
                    {renderDivider()}
                    {(slots.description || slots.action) && (
                      <div
                        className={twMerge(
                          'flex w-full items-end justify-between',
                          sizeClassMap.descriptionRowGap[size]
                        )}
                      >
                        <div className="min-w-0 flex-1">{slots.description}</div>
                        {slots.action}
                      </div>
                    )}
                  </div>
                )}

                {!isSponsor && isLarge && (
                  <div className="flex flex-col gap-[16px]">
                    {renderDivider()}
                    <div className="flex w-full items-center gap-[20px]">
                      <div className="min-w-0">{slots.detailMeta}</div>
                      <div className="min-w-0 flex-1">{slots.code}</div>
                    </div>
                    {renderDivider()}
                    <div className="flex w-full items-end justify-between gap-[20px]">
                      <div className="min-w-0 flex-1">{slots.description}</div>
                      {slots.action}
                    </div>
                  </div>
                )}

                {!isSponsor && isResponsive && (
                  <>
                    <div className="flex flex-col gap-[12px] md:hidden">
                      {renderDivider()}
                      {slots.detailMeta && (
                        <div className="flex w-full items-center">{slots.detailMeta}</div>
                      )}
                      {renderDivider()}
                      {slots.code && (
                        <div
                          className={twMerge(
                            'flex w-full items-center',
                            sizeClassMap.codeRowGap[size]
                          )}
                        >
                          {slots.code}
                        </div>
                      )}
                      {renderDivider()}
                      {(slots.description || slots.action) && (
                        <div
                          className={twMerge(
                            'flex w-full items-end justify-between',
                            sizeClassMap.descriptionRowGap[size]
                          )}
                        >
                          <div className="min-w-0 flex-1">{slots.description}</div>
                          {slots.action}
                        </div>
                      )}
                    </div>

                    <div className="hidden flex-col gap-[16px] md:flex">
                      {renderDivider()}
                      <div className="flex w-full items-center gap-[20px]">
                        <div className="min-w-0">{slots.detailMeta}</div>
                        <div className="min-w-0 flex-1">{slots.code}</div>
                      </div>
                      {renderDivider()}
                      <div className="flex w-full items-end justify-between gap-[20px]">
                        <div className="min-w-0 flex-1">{slots.description}</div>
                        {slots.action}
                      </div>
                    </div>
                  </>
                )}

                {isSponsor && isSmall && (
                  <div className="flex flex-col gap-[16px]">
                    <div className="flex flex-col gap-[12px]">
                      {renderDivider()}
                      <div
                        className={twMerge(
                          'flex w-full items-center',
                          sizeClassMap.codeRowGap[size]
                        )}
                      >
                        {slots.code}
                      </div>
                      {renderDivider()}
                    </div>
                    <div
                      className={twMerge(
                        'flex w-full items-end',
                        sizeClassMap.sponsorBottomRowGap[size]
                      )}
                    >
                      <div className="min-w-0 flex-1">{slots.detailMeta}</div>
                      {sponsorMobileAction}
                    </div>
                  </div>
                )}

                {isSponsor && isLarge && (
                  <div className="flex flex-col gap-[16px]">
                    {renderDivider()}
                    <div className="flex w-full items-center justify-between gap-[20px]">
                      <div className="min-w-0 flex-1">{slots.code}</div>
                      {sponsorDesktopAction}
                    </div>
                  </div>
                )}

                {isSponsor && isResponsive && (
                  <>
                    <div className="flex flex-col gap-[16px] md:hidden">
                      <div className="flex flex-col gap-[12px]">
                        {renderDivider()}
                        <div
                          className={twMerge(
                            'flex w-full items-center',
                            sizeClassMap.codeRowGap[size]
                          )}
                        >
                          {slots.code}
                        </div>
                        {renderDivider()}
                      </div>
                      <div
                        className={twMerge(
                          'flex w-full items-end',
                          sizeClassMap.sponsorBottomRowGap[size]
                        )}
                      >
                        <div className="min-w-0 flex-1">{slots.detailMeta}</div>
                        {sponsorMobileAction}
                      </div>
                    </div>

                    <div className="hidden flex-col gap-[16px] md:flex">
                      {renderDivider()}
                      <div className="flex w-full items-center justify-between gap-[20px]">
                        <div className="min-w-0 flex-1">{slots.code}</div>
                        {sponsorDesktopAction}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </RadixAccordion.AccordionContent>
          </article>
        </RadixAccordion.AccordionItem>
      </RadixAccordion.Root>
    </JoinLeagueCardContext.Provider>
  );
};

JoinLeagueCard.Title = Title;
JoinLeagueCard.Pill = Pill;
JoinLeagueCard.Meta = Meta;
JoinLeagueCard.MetaItem = MetaItem;
JoinLeagueCard.DetailMeta = DetailMeta;
JoinLeagueCard.DetailMetaItem = DetailMetaItem;
JoinLeagueCard.Code = Code;
JoinLeagueCard.Description = Description;
JoinLeagueCard.Action = Action;
